import React, { ReactElement, useCallback, useMemo } from 'react'
import { Tabs, Tab } from '@blueprintjs/core'
import { focusAtom, useSelector } from 'klyva'
import styled from 'styled-components'

import OrderColumnsPane from './order-columns-pane'
import { BaseColumnEntry, ColumnSettingsAtom } from './types'
import ToggleColumnsPane from './toggle-columns-pane'
import { Box } from '..'
const PanelWrapper = styled.div`
  max-height: fit-content;
  overflow: hidden;
  display: flex;
`

function ColumnSettingsViewer<
  T extends string,
  U extends BaseColumnEntry<T>,
  BulkToggleKey extends string,
  OrderOptionKey extends string,
>({
  columnSettingsAtom,
  numColumnsForToggles,
  togglingDisabledColumns,
}: {
  columnSettingsAtom: ColumnSettingsAtom<T, U, BulkToggleKey, OrderOptionKey>
  numColumnsForToggles: number
  togglingDisabledColumns: Set<T>
}) {
  const labelsForIds = useSelector(
    columnSettingsAtom,
    ({ columns }) =>
      Object.fromEntries(
        columns.map(({ id, label }) => [id, label]),
      ) as Partial<Record<T, string>>,
  )
  const renderColumnLabel = useCallback(
    (id: T): ReactElement | null => {
      return <>{labelsForIds?.[id] ?? `[${id}]`}</>
    },
    [labelsForIds],
  )
  const bulkTogglesConfig = useSelector(
    columnSettingsAtom,
    ({ bulkTogglesConfig }) => bulkTogglesConfig,
  )
  const columns: U[] = useSelector(columnSettingsAtom, ({ columns }) => columns)
  const enabledColumnIdsAtom = useMemo(
    () => focusAtom(columnSettingsAtom, (o) => o.prop('enabledColumnIds')),
    [columnSettingsAtom],
  )
  const orderOptionsConfig = useSelector(
    columnSettingsAtom,
    ({ orderOptionsConfig }) => orderOptionsConfig,
  )
  const currentOrderOptionAtom = useMemo(
    () => focusAtom(columnSettingsAtom, (o) => o.prop('currentOrderOption')),
    [columnSettingsAtom],
  )
  const currentOrderOption = useSelector(
    currentOrderOptionAtom,
    (currentOrderOption) => currentOrderOption,
  )

  return (
    <Box p="medium">
      <Tabs>
        <Tab
          id="selectColumns"
          title="Select Columns"
          panel={
            <PanelWrapper>
              <ToggleColumnsPane
                {...{
                  columns,
                  bulkTogglesConfig,
                  enabledColumnIdsAtom,
                  numColumnsForToggles,
                  currentOrderOption,
                  orderOptionsConfig,
                  togglingDisabledColumns,
                }}
              />
            </PanelWrapper>
          }
        />
        <Tab
          id="orderColumns"
          title="Order Columns"
          panel={
            <PanelWrapper>
              <OrderColumnsPane
                {...{
                  renderColumnLabel,
                  enabledColumnIdsAtom,
                  currentOrderOptionAtom,
                  orderOptionsConfig,
                }}
              />
            </PanelWrapper>
          }
        />
      </Tabs>
    </Box>
  )
}
export default ColumnSettingsViewer
